import React, { useState, useEffect, useMemo } from 'react';
import {
  BoxV2 as Box,
  InsightCard,
  Table,
  TableFilterV2 as TableFilter,
  useTable,
  useFilters,
  FilterType,
  Grid,
  Drawer,
  Badge,
} from 'portal-commons';
import {
  faTrademark,
  faSignature,
  faFontAwesome,
} from '@fortawesome/pro-regular-svg-icons';

import PageContainer from '@/components/PageContainer';
import PageTitle from '@/components/PageTitle';
import { Brand, brandController, enumController } from '@/apis/core';
import { ENTITY_TYPES } from '@/constants';
import { STATUS_BADGE_COLOR_MAP } from '@/constants/enum-mapping';
import { Loader } from '@/shared_elements';
import { getBrandNameAutocompleteOptions } from '@/apis/services';
import { humanize } from '@/utils/string';
import BrandDetails from '../BrandDetail/BrandDetailDE';

const TABLE_HEAD_ROWS = [
  { id: 'uid', label: 'BRAND ID', sortable: true },
  { id: 'companyName', label: 'LEGAL NAME', sortable: true },
  { id: 'displayName', label: 'DBA NAME', sortable: false },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: false },
  { id: 'identityStatus', label: 'IDENTITY STATUS', sortable: false },
  { id: 'trademarkStatus', label: 'TRADEMARK STATUS', sortable: false },
  { id: 'loaStatus', label: 'LOA STATUS', sortable: false },
];

const FILTER_LOADERS = {
  suggestion: {
    displayName: {
      minLength: 2,
      load: (value: string) =>
        getBrandNameAutocompleteOptions('displayName', value, 'ACTIVE'),
    },
    companyName: {
      minLength: 2,
      load: (value: string) =>
        getBrandNameAutocompleteOptions('companyName', value, 'ACTIVE'),
    },
  },
};

const FILTER_CONFIG = {
  brandUid: {
    type: FilterType.Search,
    label: 'Brand ID',
    placeholder: 'Enter Brand ID',
  },
  companyName: {
    type: FilterType.Search,
    label: 'Legal Name',
    placeholder: 'Enter Legal Name',
    suggestions: [],
  },
  displayName: {
    type: FilterType.Search,
    label: 'DBA Name',
    placeholder: 'Enter DBA Name',
    suggestions: [],
  },
  entityType: {
    type: FilterType.Dropdown,
    options: ENTITY_TYPES.map((entity) => ({
      ...entity,
      label: humanize(entity.value),
    })).filter((entity) =>
      ['PUBLIC_PROFIT', 'PRIVATE_PROFIT'].includes(entity.value)
    ),
    label: 'Entity Type',
  },
  identityStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Identity Status',
  },
  trademarkStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'Trademark Status',
  },
  loaStatus: {
    type: FilterType.Dropdown,
    options: [],
    label: 'LOA Status',
  },
};

const MAX_DISPLAY_RECORDS = 10000;

const BrandsListDE: React.FC = () => {
  const [statistics, setStatistics] = useState<{
    notSubmittedTrademarks: number;
    signedLOAs: number;
    activeBrands: number;
  }>({
    notSubmittedTrademarks: 0,
    signedLOAs: 0,
    activeBrands: 0,
  });
  const [viewingBrand, setViewingBrand] = useState<string | null>(null);
  const {
    configs: filterConfigs,
    appliedFilters,
    candidateFilters,
    additionalFilters,
    setOptions,
    handleEdit,
    handleApply,
  } = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const fetchFilterOptions = async () => {
    const [identityStatusesResult, loaStatusesResult, trademarkStatusesResult] =
      await Promise.allSettled([
        enumController.getIdentityStatus(),
        enumController.getLoaStatus(),
        enumController.getTrademarkStatus(),
      ]);

    if (
      identityStatusesResult.status === 'fulfilled' &&
      identityStatusesResult.value?.length > 0
    ) {
      setOptions(
        'identityStatus',
        identityStatusesResult.value
          // FR-556: Only show UNVERIFIED and VERIFIED for Germany market
          .filter((status: string) =>
            ['UNVERIFIED', 'VERIFIED'].includes(status)
          )
          .map((status: string) => ({
            label: humanize(status),
            value: status,
          }))
      );
    }

    if (
      loaStatusesResult.status === 'fulfilled' &&
      loaStatusesResult.value?.length > 0
    ) {
      setOptions(
        'loaStatus',
        loaStatusesResult.value.map((status: string) => ({
          label: humanize(status),
          value: status,
        }))
      );
    }

    if (
      trademarkStatusesResult.status === 'fulfilled' &&
      trademarkStatusesResult.value?.length > 0
    ) {
      setOptions(
        'trademarkStatus',
        trademarkStatusesResult.value.map((status: string) => ({
          label: humanize(status),
          value: status,
        }))
      );
    }
  };

  const fetchStatistics = async () => {
    const baseQuery = {
      status: 'ACTIVE',
      market: 'DE',
    };
    const [activeBrandsResult, signedLoaResult, pendingTrademarkResult] =
      await Promise.all([
        brandController.getBrands({
          ...baseQuery,
        }),
        brandController.getBrands({
          ...baseQuery,
          loaStatus: 'COMPLETED',
        }),
        brandController.getBrands({
          ...baseQuery,
          trademarkStatus: 'NOT_SUBMITTED',
        }),
      ]);

    setStatistics((prev) => ({
      ...prev,
      activeBrands: activeBrandsResult?.totalRecords || 0,
      signedLOAs: signedLoaResult?.totalRecords || 0,
      notSubmittedTrademarks: pendingTrademarkResult?.totalRecords || 0,
    }));
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchStatistics();
  }, []);

  const table = useTable<Brand>(
    async () => {
      const queries = {
        ...table.filter,
        ...appliedFilters,
        ...additionalFilters,
      };
      const response = await brandController.getBrands({
        ...queries,
        ...additionalFilters,
        status: 'ACTIVE',
        market: 'DE',
      });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      cappingTotal: MAX_DISPLAY_RECORDS,
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const tableData = useMemo(() => {
    const wrap = (value: string, testId: string) =>
      value?.length > 0 ? (
        <Box
          sx={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            maxWidth: '350px',
          }}
          data-testid={testId}
        >
          {value}
        </Box>
      ) : null;

    const fallbackTo = (
      value: React.ReactNode,
      testId: string,
      fallbackValue = 'N/A'
    ) => value || <span data-testid={testId}>{fallbackValue}</span>;

    const renderStatus = (
      status: string | null | undefined,
      testId: string
    ) => {
      if (!status) return <span data-testid={testId}>N/A</span>;
      const type = STATUS_BADGE_COLOR_MAP[status] || 'neutral';
      return (
        <Badge
          size="md"
          shape="rectangle"
          variant="fill"
          type={type}
          data-testid={testId}
        >
          {humanize(status)}
        </Badge>
      );
    };

    return table.tableData.map((data) => ({
      rawUid: data.uid,
      uid: <span data-testid="uid">{data.uid}</span>,
      companyName: fallbackTo(
        wrap(data.companyName, 'companyName'),
        'companyName'
      ),
      displayName: fallbackTo(wrap(data.displayName, 'dbaName'), 'dbaName'),
      entityType: fallbackTo(
        data.entityType ? (
          <span data-testid="entityType">{humanize(data.entityType)}</span>
        ) : null,
        'entityType'
      ),
      identityStatus: renderStatus(data.identityStatus, 'identityStatus'),
      trademarkStatus: renderStatus(data.trademarkStatus, 'trademarkStatus'),
      loaStatus: renderStatus(data.loaStatus, 'loaStatus'),
    }));
  }, [table.tableData]);

  if (table.pageLoading) return <Loader />;

  return (
    <PageContainer sx={{ paddingX: '96px', rowGap: null }}>
      <PageTitle>Brands</PageTitle>
      <Grid
        sx={{
          width: '100%',
          gap: '24px',
          gridTemplateColumns: 'repeat(4, 1fr)',
          marginTop: '26px',
          marginBottom: '56px',
        }}
      >
        <InsightCard
          title="TRADEMARKS NOT SUBMITTED"
          value={statistics.notSubmittedTrademarks}
          icon={faTrademark}
          data-testid="trademarksNotSubmitted"
        />
        <InsightCard
          title="SIGNED LOAs"
          value={statistics.signedLOAs}
          icon={faSignature}
          data-testid="signedLoas"
        />
        <InsightCard
          title="TOTAL ACTIVE BRANDS"
          value={statistics.activeBrands}
          icon={faFontAwesome}
          data-testid="totalActiveBrands"
        />
      </Grid>

      <Box sx={{ mb: 'm', width: '100%' }}>
        <TableFilter
          configs={filterConfigs}
          candidateValues={candidateFilters}
          appliedValues={appliedFilters}
          onCandidateValuesChange={handleEdit}
          onAppliedValuesChange={handleApply}
          data-testid="tableFilters"
        />
      </Box>

      <Table
        onRowClick={(data) => setViewingBrand(data.rawUid as string)}
        loading={table.loading}
        headRows={TABLE_HEAD_ROWS}
        emptyState="no brands to view"
        data={tableData}
        rowKey="uid"
        handleChangePage={table.handleChangePage}
        createSortHandler={table.handleSorting}
        filter={table.filter}
        pagination={table.pagination}
        emptyBlockHeight="180px"
        data-testid="brandsTable"
      />
      <Drawer
        open={viewingBrand !== null}
        onClose={() => setViewingBrand(null)}
        width="70%"
      >
        <BrandDetails brandUid={viewingBrand} />
      </Drawer>
    </PageContainer>
  );
};

export default BrandsListDE;
